/**
 * One list of every document (owner and Dustin, 2026-10-30: the All, Needs review and Teach filters
 * went; docs/planning/every-document-calibration.md). The list always shows every document; what it
 * asks the operator about depends on the job's Calibration. Review work is always asked about. While
 * the job is calibrating, its teaching examples are asked about too, in the same look and without
 * saying which is which: early on, the reason is no fair picture of the job.
 */
type WorkState = { id?: string; needsReview: boolean; needsTeaching?: boolean };

/** Why a document is asked about: review work, a teaching example while calibrating, or not at all. */
export type AskedWork = 'review' | 'teach';
export const askedWork = (document: WorkState, calibrating: boolean): AskedWork | null =>
  document.needsReview ? 'review' : calibrating && document.needsTeaching ? 'teach' : null;

/**
 * The next document asked about after `current`, in list order, wrapping to the top: an operator who
 * works from the middle down is not told there is nothing left while documents above still wait.
 * `count` is how many are asked about (the current one included); `index` is null when none other is.
 */
export function nextAsked<T extends WorkState>(
  documents: readonly T[],
  current: number,
  calibrating: boolean,
  available: (document: T) => boolean = () => true,
): { count: number; index: number | null } {
  let count = 0,
    after: number | null = null,
    before: number | null = null;
  documents.forEach((document, index) => {
    if (!available(document) || askedWork(document, calibrating) === null) return;
    count += 1;
    if (index > current) after ??= index;
    else if (index < current) before ??= index;
  });
  return { count, index: after ?? before };
}

/**
 * Autopilot (ai-reviewed) jobs: whether a waiting document is still the AI reviewer's to settle. Once the reviewer
 * has finished (aiReviewUnsettled set), what still holds it is a person's: not read, no type, no values, or their
 * own unconfirmed change. Before then, only not read, no values or a person's change make it a person's; the
 * reviewer chooses a type for an untyped document itself.
 */
export function awaitsAiReviewer(document: {
  confirmedAsWhole?: boolean;
  aiReviewUnsettled?: readonly string[] | null;
  waitReasons?: readonly string[] | null;
}): boolean {
  if (document.confirmedAsWhole || document.aiReviewUnsettled != null) return false;
  const reasons = document.waitReasons ?? [];
  return !reasons.some(
    (reason) => reason === 'not-read' || reason === 'no-values' || reason === 'change-unconfirmed',
  );
}
